import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, LineChart, LogOut, Calendar as CalendarIcon, User, HelpCircle, Crown, Heart, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AnimatePresence } from "framer-motion";
import ProfileModal from "@/components/ProfileModal";

export default function Header() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isTrends = loc.pathname === "/trends";
  const [showProfile, setShowProfile] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const isPro = user?.pro_status === "active" || user?.pro_status === "trialing";

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/customer-portal", { return_url: window.location.origin });
      window.location.assign(data.portal_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open the subscription portal");
      setPortalLoading(false);
    }
  };
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-kawaii-border">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="app-logo">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-kawaii-pink to-kawaii-peach flex items-center justify-center shadow-sm">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-kawaii-text leading-none">MyLymphieLife</div>
            <div className="text-xs text-kawaii-textSoft/70 mt-0.5 tracking-wide">your gentle daily companion</div>
          </div>
        </Link>
        <nav className="flex items-center gap-2">
          <Link
            to="/"
            data-testid="nav-calendar"
            className={`kawaii-btn px-4 py-2 rounded-full font-body text-sm font-semibold inline-flex items-center gap-2 ${!isTrends ? 'bg-kawaii-pink text-white' : 'bg-white text-kawaii-text border border-kawaii-border'}`}
          >
            <CalendarIcon className="w-4 h-4" strokeWidth={2.5} /> Calendar
          </Link>
          <Link
            to="/trends"
            data-testid="nav-trends"
            className={`kawaii-btn px-4 py-2 rounded-full font-body text-sm font-semibold inline-flex items-center gap-2 ${isTrends ? 'bg-kawaii-lavender text-white' : 'bg-white text-kawaii-text border border-kawaii-border'}`}
          >
            <LineChart className="w-4 h-4" strokeWidth={2.5} /> Trends
          </Link>
          <div className="hidden md:flex items-center gap-2 pl-3 ml-2 border-l border-kawaii-border">
            <span className="text-sm text-kawaii-textSoft font-body">Hi, {user?.name} ♡</span>
            {(user?.pro_status === "active" || user?.pro_status === "trialing") && (
              <span
                data-testid="pro-badge"
                title={user?.pro_status === "trialing" ? "Pro trial" : "Pro"}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-kawaii-lavender to-kawaii-pink text-white"
              >
                <Crown className="w-3 h-3" strokeWidth={2.5} /> Pro
              </span>
            )}
            {user?.pro?.source === "trial" && user?.pro_status !== "active" && user?.pro_status !== "trialing" && (
              <span
                data-testid="trial-badge"
                title={`Free trial — ${user.pro.trial_days_left} day(s) left`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white border border-kawaii-lavender/60 text-kawaii-text"
              >
                <Crown className="w-3 h-3 text-kawaii-lavender" strokeWidth={2.5} /> Trial · {user.pro.trial_days_left}d
              </span>
            )}
            {isPro && (
              <button
                onClick={openCustomerPortal}
                disabled={portalLoading}
                data-testid="manage-subscription-btn"
                title="Manage subscription (cancel, switch plan, invoices)"
                className="kawaii-btn p-2 rounded-full bg-white border border-kawaii-border text-kawaii-text hover:bg-kawaii-border disabled:opacity-60"
              >
                <Settings2 className="w-4 h-4" strokeWidth={2.5} />
              </button>
            )}
            {user?.is_supporter && !(user?.pro_status === "active" || user?.pro_status === "trialing") && (
              <span
                data-testid="supporter-badge"
                title="Supporter — thank you ♡"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-kawaii-pink/20 text-kawaii-text border border-kawaii-pink/40"
              >
                <Heart className="w-3 h-3 text-kawaii-pink" strokeWidth={2.5} fill="#F4A8CD" /> Supporter
              </span>
            )}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("replay-tour"))}
              data-testid="replay-tour-btn"
              className="kawaii-btn p-2 rounded-full bg-white border border-kawaii-border text-kawaii-text hover:bg-kawaii-border"
              title="Replay tour"
            >
              <HelpCircle className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={() => setShowProfile(true)}
              data-testid="open-profile-btn"
              className="kawaii-btn p-2 rounded-full bg-white border border-kawaii-border text-kawaii-text hover:bg-kawaii-border"
              title="Medical passport"
            >
              <User className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="kawaii-btn p-2 rounded-full bg-white border border-kawaii-border text-kawaii-text hover:bg-kawaii-border"
              title="Log out"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </nav>
      </div>
      <AnimatePresence>
        {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
      </AnimatePresence>
    </header>
  );
}
